<template>
  <BaseModal fullscreen :title="modalTitle" :value="open" @close="close" headerClass="py-3">
    <template>
      <form @submit.prevent="handleSubmit" class="space-y-4 py-4">
        <!-- Title fields for each language -->
        <div v-for="(lang, i) in arrayLang" :key="i" class="space-y-1">
          <label class="block text-sm font-medium text-gray-700">
            {{ translate("Title - _LANG_", { _LANG_: translate(lang) })
            }}<span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            v-model="page.title[lang]"
            :placeholder="
              translate('Enter title.. - _LANG_', { _LANG_: translate(lang) })
            "
            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            required
          />
          <p
            v-if="showValidationErrors && validationErrors.title[lang]"
            class="text-red-500 text-xs mt-1"
          >
            {{ translate("This field is required") }}
          </p>
        </div>

        <!-- Page Name -->
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-700">
            {{ translate(existingPage ? "Page Name" : "New Page Name")
            }}<span class="text-red-500">*</span>
          </label>
          <input
            type="text"
            v-model="page.label"
            :placeholder="translate('Enter page name')"
            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            required
          />
          <p
            v-if="showValidationErrors && validationErrors.label"
            class="text-red-500 text-xs mt-1"
          >
            {{ getLabelErrorMessage() }}
          </p>
        </div>

        <!-- Page type -->
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-700">
            {{ translate("Type") }}<span class="text-red-500">*</span>
          </label>
          <select
            v-model="page.type"
            class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            required
          >
            <option v-for="(type, i) in Types" :key="i" :value="type.value">
              {{ type.text }}
            </option>
          </select>
          <p
            v-if="showValidationErrors && validationErrors.type"
            class="text-red-500 text-xs mt-1"
          >
            {{ translate("This field is required") }}
          </p>
        </div>

        <!-- URL or HTML -->
        <div class="space-y-1">
          <label class="block text-sm font-medium text-gray-700">
            {{ page.type === 'link' ? translate("URL") : translate("Content") }}<span class="text-red-500">*</span>
          </label>

          <!-- URL Input for Link type -->
          <input
            v-if="page.type === 'link'"
            type="url"
            v-model="page.url"
            :placeholder="translate('Enter URL (e.g., https://example.com)')"
            class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            required
          />

          <!-- Rich Text Editor for Page type -->
          <div v-else-if="page.type === 'page'" class="mt-1">
            <div ref="editor" class="border border-gray-300 rounded-md shadow-sm"></div>
            <!-- Character counter -->
            <div class="mt-2 flex justify-between items-center text-sm">
              <span class="text-gray-500">
                {{ translate("Characters") }}: {{ getContentCharacterCount() }}
              </span>
              <span
                :class="{
                  'text-red-500': getContentCharacterCount() > 25000,
                  'text-yellow-500': getContentCharacterCount() > 20000 && getContentCharacterCount() <= 25000,
                  'text-gray-500': getContentCharacterCount() <= 20000
                }"
              >
                {{ translate("Limit") }}: 25,000
              </span>
            </div>
            <!-- Character limit warning -->
            <p
              v-if="getContentCharacterCount() > 25000"
              class="text-red-500 text-xs mt-1"
            >
              {{ translate("Content cannot exceed 25,000 characters") }}
            </p>
            <!-- Approaching limit warning -->
            <p
              v-else-if="getContentCharacterCount() > 20000"
              class="text-yellow-500 text-xs mt-1"
            >
              {{ translate("Content is approaching the 25,000 character limit") }}
            </p>
          </div>

          <p
            v-if="showValidationErrors && validationErrors.url"
            class="text-red-500 text-xs mt-1"
          >
            {{ getUrlErrorMessage() }}
          </p>
        </div>

        <!-- Submit button -->
        <div class="mt-5 sm:mt-6 text-center">
          <button
            :id="existingPage ? 'pages.updatepage' : 'pages.createpage'"
            type="submit"
            class="inline-flex justify-center w-1/2 rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
            :class="{
              'opacity-50 cursor-not-allowed': isSubmitting || !isFormValid,
            }"
            :disabled="isSubmitting || !isFormValid"
          >
            <i v-if="isSubmitting" class="fas fa-spinner fa-spin mr-2"></i>
            {{ submitButtonText }}
          </button>
        </div>
      </form>
    </template>
  </BaseModal>
</template>

<script>
import { translations } from '@/mixins';
import { Page as PageApi } from '@/services/SOLO';
import BaseModal from '@/components/V2/Modals/BaseModal.vue';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

export default {
  name: 'PageModal',

  components: {
    BaseModal,
  },

  mixins: [translations],

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    existingPage: {
      type: [Object, null],
      default: null,
    },

  },

  data () {
    return {
      page: this.existingPage || {
        label: '',
        url: '',
        title: {},
        type: 'link',
      },
      pageTitle: {},
      arrayLang: [],
      Types: [
        { text: 'Page', value: 'page' },
        { text: 'Link', value: 'link' },
      ],
      isSubmitting: false,
      validationErrors: {
        title: {},
        type: false,
        label: false,
        url: false,
      },
      showValidationErrors: false,
      quillEditor: null,
    };
  },

  computed: {
    modalTitle () {
      return this.translate(this.existingPage ? 'Edit Page' : 'Add Page');
    },

    submitButtonText () {
      return this.translate(this.existingPage ? 'UPDATE' : 'SAVE');
    },

    isFormValid () {
      const hasTitleErrors = this.arrayLang.some(
        (lang) => !this.page.title[lang],
      );
      const hasTypeError = !this.page.type;

      // Check label validation
      let hasLabelError = false;
      if (!this.page.label) {
        hasLabelError = true;
      } else {
        const reservedLabels = ['privacy-policy', 'personal-data-processing'];
        hasLabelError = reservedLabels.includes(this.page.label.toLowerCase());
      }

      // For page type, check if content is not just empty HTML tags
      let hasUrlError = false;
      if (this.page.type === 'page') {
        const content = this.page.url.replace(/<[^>]*>/g, '').trim();
        const isOnlyNumbers = !!content && /^[0-9\s]+$/.test(content);
        hasUrlError = !content || content.length > 25000 || isOnlyNumbers;
      } else if (this.page.type === 'link') {
        hasUrlError = !this.page.url || !this.isValidUrl(this.page.url);
      }

      return !hasTitleErrors && !hasTypeError && !hasLabelError && !hasUrlError;
    },

    pageData () {
      this.pageTitle = {};

      for (const i in this.arrayLang) {
        this.pageTitle[this.arrayLang[i]] = this.page.title[this.arrayLang[i]];
      }

      return {
        title: this.pageTitle,
        label: this.page.label,
        type: this.page.type,
        url: this.page.url,
      };
    },

    open () {
      return this.isOpen;
    },
  },

  mounted () {
    // Get supported languages from concept settings
    if (this.getConceptSettings?.attributes?.['primary-language']) {
      this.arrayLang.push(
        this.getConceptSettings.attributes['primary-language'],
      );
    }

    if (this.getConceptSettings?.attributes?.['secondary-language']) {
      this.arrayLang.push(
        this.getConceptSettings.attributes['secondary-language'],
      );
    }

    if (this.existingPage) return;

    // Initialize title object with empty values for each language
    this.arrayLang.forEach((lang) => {
      this.$set(this.page.title, lang, '');
    });
  },

  watch: {
    'page.type': {
      handler (newType) {
        this.$nextTick(() => {
          if (newType === 'page') {
            // When switching to page type for a new page, clear any leftover URL value
            if (!this.existingPage) {
              this.$set(this.page, 'url', '');
            }
            this.initQuillEditor();
          } else {
            this.destroyQuillEditor();
          }
        });
      },
      immediate: true,
    },
  },

  beforeDestroy () {
    this.destroyQuillEditor();
  },

  methods: {
    close () {
      this.$emit('close');
      this.resetFields();
    },

    initQuillEditor () {
      // Clean up existing editor
      this.destroyQuillEditor();

      if (this.$refs.editor && this.page.type === 'page') {
        this.quillEditor = new Quill(this.$refs.editor, {
          theme: 'snow',
          modules: {
            toolbar: [
              [{ header: [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ list: 'ordered' }, { list: 'bullet' }],
              [{ color: [] }, { background: [] }],
              [{ align: [] }],
              ['link', 'image'],
              ['clean'],
            ],
          },
          placeholder: this.translate('Enter page content...'),
        });

        // Set initial content from current model value (if any)
        if (typeof this.page.url === 'string') {
          this.quillEditor.root.innerHTML = this.page.url || '';
        }

        // Update page.url when content changes
        this.quillEditor.on('text-change', () => {
          const applyUpdate = () => {
            this.$set(this.page, 'url', this.quillEditor.root.innerHTML);

            // Use plain text content for validation/feedback
            const content = this.quillEditor.getText().trim();
            if (content.length > 25000) {
              // Add visual feedback to the editor
              this.quillEditor.root.classList.add('character-limit-warning');
              this.quillEditor.root.classList.remove('character-limit-approaching');
            } else if (content.length > 20000) {
              // Warning when approaching limit
              this.quillEditor.root.classList.add('character-limit-approaching');
              this.quillEditor.root.classList.remove('character-limit-warning');
            } else {
              // Reset border style
              this.quillEditor.root.classList.remove('character-limit-warning');
              this.quillEditor.root.classList.remove('character-limit-approaching');
            }
          };

          if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(applyUpdate);
          } else {
            setTimeout(applyUpdate, 0);
          }
        });
      }
    },

    destroyQuillEditor () {
      if (this.quillEditor) {
        // Remove the editor from DOM
        if (this.$refs.editor) {
          this.$refs.editor.innerHTML = '';
        }
        this.quillEditor = null;
      }
    },

    getLabelErrorMessage () {
      if (!this.page.label) {
        return this.translate('This field is required');
      }

      const reservedLabels = ['privacy-policy', 'personal-data-processing'];
      if (reservedLabels.includes(this.page.label.toLowerCase())) {
        return this.translate('This page name is reserved and cannot be used');
      }

      return this.translate('This field is required');
    },

    isValidUrl (string) {
      try {
        const url = new URL(string);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch (_) {
        return false;
      }
    },

    getUrlErrorMessage () {
      if (this.page.type === 'link') {
        if (!this.page.url) {
          return this.translate('URL is required');
        } else if (!this.isValidUrl(this.page.url)) {
          return this.translate('Please enter a valid URL (e.g., https://example.com)');
        }
      } else if (this.page.type === 'page') {
        const content = this.page.url.replace(/<[^>]*>/g, '').trim();
        const isOnlyNumbers = !!content && /^[0-9\s]+$/.test(content);
        if (!content) {
          return this.translate('Content is required');
        } else if (isOnlyNumbers) {
          return this.translate('Content cannot consist of numbers only');
        } else if (content.length > 25000) {
          return this.translate('Content cannot exceed 25,000 characters');
        }
      }
      return this.translate('This field is required');
    },

    getContentCharacterCount () {
      if (!this.page.url) {
        return 0;
      }
      // Count plain text characters (strip HTML) to match validation logic
      const content = this.page.url.replace(/<[^>]*>/g, '').trim();
      return content.length;
    },

    resetFields () {
      this.destroyQuillEditor();

      if (!this.existingPage) {
        this.page = {
          label: '',
          url: '',
          title: {},
          type: '',
        };

        // Re-initialize title object with empty values
        this.arrayLang.forEach((lang) => {
          this.$set(this.page.title, lang, '');
        });
      }
    },

    async handleSubmit () {
      // Ensure editor content is captured before validation/submission
      if (this.page.type === 'page' && this.quillEditor) {
        this.$set(this.page, 'url', this.quillEditor.root.innerHTML);
      }

      this.validateFields();

      if (!this.isFormValid) {
        this.showValidationErrors = true;
        return;
      }

      if (this.existingPage) {
        await this.updatePage();
      } else {
        await this.createPage();
      }
    },

    async createPage () {
      this.isSubmitting = true;

      try {
        await PageApi.add(this.pageData);

        this.$notify({
          title: 'PAGE CREATED!',
          verticalAlign: 'bottom',
          horizontalAlign: 'left',
          message: 'Page has been created successfully',
          type: 'success',
          icon: 'fas fa-check',
        });

        this.resetFields();
        this.$emit('getPages', 1, 10);
        this.$emit('close');
      } catch (error) {
        this.$notify({
          title: 'SYSTEM ERROR!',
          verticalAlign: 'bottom',
          horizontalAlign: 'left',
          message: 'Something went wrong, please try again!',
          type: 'danger',
          icon: 'fas fa-bomb',
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    async updatePage () {
      this.isSubmitting = true;

      try {
        const response = await PageApi.update(
          this.pageData,
          this.existingPage.id,
        );

        this.$notify({
          title: 'PAGE UPDATED!',
          verticalAlign: 'bottom',
          horizontalAlign: 'left',
          message: 'Page has been updated successfully',
          type: 'success',
          icon: 'fas fa-check',
        });

        this.$emit('updatePage', response.data.data);
        this.close();
      } catch (error) {
        this.$notify({
          title: 'SYSTEM ERROR!',
          verticalAlign: 'bottom',
          horizontalAlign: 'left',
          message: 'Something went wrong, please try again!',
          type: 'danger',
          icon: 'fas fa-bomb',
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    /**
     * Validate all form fields and set error flags
     */
    validateFields () {
      // Reset validation errors
      this.validationErrors = {
        title: {},
        type: false,
        label: false,
        url: false,
      };

      // Check each field
      if (!this.page.type) {
        this.validationErrors.type = true;
      }

      if (!this.page.label) {
        this.validationErrors.label = true;
      } else {
        // Check if label is not a reserved value
        const reservedLabels = ['privacy-policy', 'personal-data-processing'];
        if (reservedLabels.includes(this.page.label.toLowerCase())) {
          this.validationErrors.label = true;
        }
      }

      // For page type, check if content is not just empty HTML tags
      if (this.page.type === 'page') {
        const content = this.page.url.replace(/<[^>]*>/g, '').trim();
        const isOnlyNumbers = !!content && /^[0-9\s]+$/.test(content);
        if (!content) {
          this.validationErrors.url = true;
        } else if (isOnlyNumbers) {
          this.validationErrors.url = true;
        } else if (content.length > 25000) {
          this.validationErrors.url = true;
        }
      } else if (this.page.type === 'link') {
        // Check if URL is not empty and is valid
        if (!this.page.url) {
          this.validationErrors.url = true;
        } else if (!this.isValidUrl(this.page.url)) {
          this.validationErrors.url = true;
        }
      }

      // Check language titles
      for (const lang of this.arrayLang) {
        if (!this.page.title[lang]) {
          this.$set(this.validationErrors.title, lang, true);
        } else {
          this.$set(this.validationErrors.title, lang, false);
        }
      }

      // Show validation errors
      this.showValidationErrors = true;
    },
  },
};
</script>

<style>
.ql-editor {
  min-height: 300px !important;
  max-height: 500px !important;
  overflow-y: auto !important;
}

.ql-container {
  min-height: 300px !important;
  max-height: 500px !important;
}

.ql-toolbar {
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
}

.ql-container {
  border-bottom-left-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

/* Character limit warning styles */
.character-limit-warning {
  border: 2px solid #ef4444 !important;
  box-shadow: 0 0 0 1px #ef4444 !important;
}

.character-limit-approaching {
  border: 2px solid #f59e0b !important;
  box-shadow: 0 0 0 1px #f59e0b !important;
}
</style>
